// src/components/layout/Header.jsx
import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Avatar,
  Badge,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  Tooltip,
} from "@mui/material";
import {
  Menu as MenuIcon,
  NotificationsNone as NotificationsNoneIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  PersonOutline as AccountIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { logoutUser } from "../../features/authSlice";
import {
  markAsRead,
  markAllAsRead,
} from "../../features/notificationSlice";

function Header({ handleDrawerToggle }) {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { unreadCount, notifications } = useSelector(
    (state) => state.notification,
  );

  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);

  const open = Boolean(anchorEl);
  const notificationOpen = Boolean(notificationAnchorEl);

  const getDisplayName = () =>
    user?.name || user?.userName || user?.firstName || "User";
  const getDisplayEmail = () => user?.email || user?.userEmail || "";

  const getInitials = (name) => {
    const parts = name.trim().split(" ");
    return parts.length >= 2
      ? (parts[0][0] + parts[1][0]).toUpperCase()
      : name.charAt(0).toUpperCase();
  };

  const handleClick = (e) => setAnchorEl(e.currentTarget);
  const handleNotificationClick = (e) =>
    setNotificationAnchorEl(e.currentTarget);

  const navigate = useNavigate();
  const handleClose = () => setAnchorEl(null);
  const handleNotificationClose = () => setNotificationAnchorEl(null);

  const handleLogout = () => {
    handleClose();
    dispatch(logoutUser());
  };

  const handleProfileClick = () => {
    handleClose();
    navigate("/profile");
  };

  const handleSettingsClick = () => {
    handleClose();
    navigate("/change-password");
  };

  const handleMarkAsRead = (id) => {
    dispatch(markAsRead(id));
  };

  const handleMarkAllAsRead = () => {
    dispatch(markAllAsRead());
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        zIndex: (t) => t.zIndex.drawer + 1,
        backgroundColor: "rgba(255,255,255,0.8)", // Semi-transparent for glass effect
        backdropFilter: "var(--glass-blur)",
        color: "var(--text-main)",
        borderBottom: "1px solid var(--glass-border)",
        boxShadow: "var(--shadow-sm)",
      }}
    >
      <Toolbar
        sx={{
          justifyContent: "space-between",
          px: { xs: 1.5, sm: 2.5 },
          minHeight: { xs: "56px !important", sm: "64px !important" },
        }}
      >
        {/* ── Left: toggle + brand ── */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Tooltip title="Toggle Sidebar" placement="bottom">
            <IconButton
              onClick={handleDrawerToggle}
              size="small"
              sx={{
                color: "var(--text-muted)",
                border: "1px solid var(--glass-border)",
                borderRadius: "10px",
                width: 36,
                height: 36,
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "var(--primary-color)",
                  color: "var(--white)",
                  borderColor: "var(--primary-hover)",
                  transform: "scale(1.05)",
                  boxShadow: "var(--neon-glow)",
                },
              }}
            >
              <MenuIcon sx={{ fontSize: "1.1rem" }} />
            </IconButton>
          </Tooltip>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              sx={{
                fontWeight: "var(--fw-bold)",
                fontSize: { xs: "1rem", sm: "1.1rem" },
                letterSpacing: "-0.5px",
                background: "var(--gradient-primary)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                display: { xs: "none", sm: "block" },
              }}
            >
              QMS
            </Typography>
          </Box>
        </Box>

        {/* ── Right: notifications + profile ── */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 0.5, sm: 1 },
          }}
        >
          <Tooltip title="Notifications" placement="bottom">
            <IconButton
              size="small"
              onClick={handleNotificationClick}
              sx={{
                color: "var(--text-muted)",
                width: 38,
                height: 38,
                border: "1px solid var(--glass-border)",
                borderRadius: "10px",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "var(--background-light)",
                  borderColor: "var(--primary-color)",
                  color: "var(--primary-color)",
                },
              }}
            >
              <Badge
                color="error"
                badgeContent={unreadCount}
                variant={unreadCount > 0 ? "standard" : "dot"}
                invisible={unreadCount === 0 && false} // Show dot even if 0 if we want it to look like the design, but user said "dynamicly"
                sx={{
                  "& .MuiBadge-badge": {
                    fontSize: "0.65rem",
                    height: 16,
                    minWidth: 16,
                    padding: "0 4px",
                  },
                  "& .MuiBadge-dot": {
                    width: 7,
                    height: 7,
                    top: 2,
                    right: 2,
                    border: "1.5px solid var(--white)",
                  },
                }}
              >
                <NotificationsNoneIcon sx={{ fontSize: "1.15rem" }} />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* Notifications Menu */}
          <Menu
            anchorEl={notificationAnchorEl}
            open={notificationOpen}
            onClose={handleNotificationClose}
            PaperProps={{
              elevation: 0,
              sx: {
                width: 320,
                maxHeight: 400,
                mt: 1.5,
                borderRadius: "14px",
                border: "1px solid var(--glass-border)",
                boxShadow: "var(--shadow-lg)",
                backgroundColor: "var(--white)",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              },
            }}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.5,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{ fontWeight: "var(--fw-bold)", fontSize: "0.9rem" }}
              >
                Notifications
              </Typography>
              {unreadCount > 0 && (
                <Typography
                  onClick={handleMarkAllAsRead}
                  sx={{
                    fontSize: "0.75rem",
                    color: "var(--primary-color)",
                    cursor: "pointer",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Mark all as read
                </Typography>
              )}
            </Box>
            <Divider />
            <Box sx={{ overflowY: "auto", flexGrow: 1, py: 0.5 }}>
              {notifications.length === 0 ? (
                <Box sx={{ p: 3, textAlign: "center" }}>
                  <Typography
                    sx={{ fontSize: "0.85rem", color: "var(--text-muted)" }}
                  >
                    No notifications yet
                  </Typography>
                </Box>
              ) : (
                notifications.map((notification) => (
                  <MenuItem
                    key={notification.id}
                    onClick={() => {
                      handleMarkAsRead(notification.id);
                      // handleNotificationClose();
                    }}
                    sx={{
                      px: 2,
                      py: 1.5,
                      borderLeft: notification.isRead
                        ? "none"
                        : "3px solid var(--primary-color)",
                      backgroundColor: notification.isRead
                        ? "transparent"
                        : "rgba(99, 102, 241, 0.04)",
                      whiteSpace: "normal",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      gap: 0.5,
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.02)",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: notification.isRead
                          ? "var(--fw-medium)"
                          : "var(--fw-bold)",
                        fontSize: "0.85rem",
                      }}
                    >
                      {notification.title}
                    </Typography>
                    <Typography
                      sx={{ fontSize: "0.8rem", color: "var(--text-muted)" }}
                    >
                      {notification.message}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        color: "var(--text-muted)",
                        mt: 0.5,
                      }}
                    >
                      {new Date(notification.timestamp).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </Typography>
                  </MenuItem>
                ))
              )}
            </Box>
          </Menu>

          {/* Profile trigger */}
          <Box
            onClick={handleClick}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              cursor: "pointer",
              pl: 1,
              pr: { xs: 0.5, sm: 1.5 },
              py: 0.75,
              borderRadius: "12px",
              border: `1px solid ${open ? "var(--primary-color)" : "var(--glass-border)"}`,
              ml: 0.5,
              transition: "all 0.2s ease",
              backgroundColor: open ? "rgba(99, 102, 241, 0.1)" : "transparent",
              "&:hover": {
                backgroundColor: "rgba(99, 102, 241, 0.05)",
                borderColor: "var(--primary-color)",
                boxShadow: "var(--shadow-sm)",
              },
            }}
          >
            <Avatar
              sx={{
                width: 30,
                height: 30,
                background: "var(--gradient-primary)",
                fontSize: "11px",
                fontWeight: "var(--fw-bold)",
                boxShadow: "var(--shadow-glow)",
                flexShrink: 0,
              }}
            >
              {getInitials(getDisplayName())}
            </Avatar>

            <Box sx={{ display: { xs: "none", sm: "block" }, minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: "var(--fw-semibold)",
                  fontSize: "var(--fs-sm)",
                  color: "var(--text-main)",
                  lineHeight: 1.2,
                }}
              >
                {getDisplayName()}
              </Typography>
            </Box>

            <KeyboardArrowDownIcon
              sx={{
                color: "var(--text-muted)",
                fontSize: "1rem",
                display: { xs: "none", sm: "block" },
                transition: "transform 0.2s ease",
                transform: open ? "rotate(180deg)" : "rotate(0deg)",
                ml: 0.5,
              }}
            />
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            PaperProps={{
              elevation: 0,
              sx: {
                width: 230,
                mt: 1.5,
                borderRadius: "14px",
                border: "1px solid var(--glass-border)",
                boxShadow: "var(--shadow-lg)",
                backgroundColor: "var(--white)",
                "&::before": {
                  content: "\"\"",
                  display: "block",
                  position: "absolute",
                  top: -6,
                  right: 20,
                  width: 12,
                  height: 12,
                  backgroundColor: "inherit",
                  transform: "rotate(45deg)",
                  borderTop: "1px solid var(--glass-border)",
                  borderLeft: "1px solid var(--glass-border)",
                },
              },
            }}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Box sx={{ px: 2, py: 1.75 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    background: "var(--gradient-primary)",
                    fontSize: "14px",
                    fontWeight: "var(--fw-bold)",
                    boxShadow: "var(--shadow-glow)",
                  }}
                >
                  {getInitials(getDisplayName())}
                </Avatar>
                <Box sx={{ overflow: "hidden" }}>
                  <Typography
                    sx={{
                      fontWeight: "var(--fw-bold)",
                      fontSize: "0.875rem",
                      color: "var(--text-main)",
                    }}
                  >
                    {getDisplayName()}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "var(--fs-xs)",
                      color: "var(--text-muted)",
                    }}
                  >
                    {getDisplayEmail()}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Divider sx={{ borderColor: "var(--background-light)" }} />

            <Box sx={{ py: 0.75 }}>
              <>
                <MenuItem
                  key="profile"
                  onClick={handleProfileClick}
                  sx={{
                    mx: 1,
                    px: 1.5,
                    py: 1,
                    borderRadius: "8px",
                    gap: 1.5,
                    fontSize: "var(--fs-sm)",
                    color: "var(--text-main)",
                    "&:hover": {
                      backgroundColor: "rgba(99, 102, 241, 0.08)",
                      color: "var(--primary-color)",
                      "& .MuiListItemIcon-root": {
                        color: "var(--primary-color)",
                      },
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{ minWidth: 0, color: "var(--text-muted)" }}
                  >
                    <AccountIcon sx={{ fontSize: "1.1rem" }} />
                  </ListItemIcon>
                  My Profile
                </MenuItem>
                <MenuItem
                  key="settings"
                  onClick={handleSettingsClick}
                  sx={{
                    mx: 1,
                    px: 1.5,
                    py: 1,
                    borderRadius: "8px",
                    gap: 1.5,
                    fontSize: "var(--fs-sm)",
                    color: "var(--text-main)",
                    "&:hover": {
                      backgroundColor: "rgba(99, 102, 241, 0.08)",
                      color: "var(--primary-color)",
                      "& .MuiListItemIcon-root": {
                        color: "var(--primary-color)",
                      },
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{ minWidth: 0, color: "var(--text-muted)" }}
                  >
                    <SettingsIcon sx={{ fontSize: "1.1rem" }} />
                  </ListItemIcon>
                  Settings
                </MenuItem>
              </>
            </Box>

            <Divider sx={{ borderColor: "var(--background-light)" }} />

            <Box sx={{ py: 0.75 }}>
              <MenuItem
                onClick={handleLogout}
                sx={{
                  mx: 1,
                  px: 1.5,
                  py: 1,
                  borderRadius: "8px",
                  gap: 1.5,
                  fontSize: "var(--fs-sm)",
                  color: "var(--accent-color)",
                  fontWeight: "var(--fw-bold)",
                  "&:hover": { backgroundColor: "rgba(244, 63, 94, 0.08)" },
                }}
              >
                <ListItemIcon
                  sx={{ minWidth: 0, color: "var(--accent-color)" }}
                >
                  <LogoutIcon sx={{ fontSize: "1.1rem" }} />
                </ListItemIcon>
                Sign Out
              </MenuItem>
            </Box>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
}

export default Header;
